"use client";

import { useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { defaultTo } from "lodash-es";
import { useCallback } from "react";
import { useTranslations, useLocale } from "use-intl";

import { CompanyUnavailableIcon, ErrorIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { Spinner } from "@/components/ui/spinner";
import { COMPANY_DISCOGRAPHY_SURFACE } from "@/facets/config";
import { useFacetFilters } from "@/facets/use-facet-filters";
import { useDeferredResourceQuery } from "@/hooks/use-deferred-resource";
import { useRecordView } from "@/hooks/use-record-view";
import { useTextInputFilter } from "@/hooks/use-text-input-filter";
import {
  COMPANY_DISCOGRAPHY_URL_CONFIG,
  buildFacetedSearchState,
  writeFacetedSearchState,
} from "@/lib/faceted-search-url";
import { buildFilterLinkTarget } from "@/lib/filter-url-builder";
import { getSingleReleaseSearchFormat } from "@/lib/release-search-format-filter";
import { useDiscographyQueryType } from "@/routes/_app/artist/$id/use-discography-query-type";
import type { DiscographyQueryType } from "@/types/discography";
import { orpc } from "@/utils/orpc.client";
import type { DiscographyTypeCounts } from "@garimpa/lib/resolveDiscographyDefaultType";
import type { FacetedSearchState } from "@garimpa/lib/searchState";

import { CompanyAbout } from "./company-about";
import { getCompanyBioResourceQueryOptions } from "./company-bio-resource-query";
import { CompanyDiscography, type SortOption } from "./company-discography";
import { CompanyHeader } from "./company-header";
import { getCompanyQueryOptions } from "./company-query";

interface CompanyContentProps {
  id: string;
  discographyQueryType: DiscographyQueryType;
  discographyTypeCounts?: DiscographyTypeCounts | null;
}

const routeApi = getRouteApi("/_app/company/$id");

export function CompanyContent({
  id,
  discographyQueryType: serverDiscographyQueryType,
  discographyTypeCounts,
}: CompanyContentProps) {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const facetState = buildFacetedSearchState(search, COMPANY_DISCOGRAPHY_URL_CONFIG);
  const activeFormat = defaultTo(getSingleReleaseSearchFormat(facetState?.filter), "all");
  const onSearchStateChange = useCallback(
    (next: FacetedSearchState | undefined) => {
      navigate({
        search: (prev) => writeFacetedSearchState(prev, next, COMPANY_DISCOGRAPHY_URL_CONFIG),
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );
  const facetControls = useFacetFilters({
    state: facetState,
    onChange: onSearchStateChange,
    surface: COMPANY_DISCOGRAPHY_SURFACE,
  });
  const updateFilter = useCallback(
    (key: string, value: string | undefined) => {
      navigate({
        search: (prev) => {
          if (key === "q") return { ...prev, q: value || undefined };
          if (key === "type") return { ...prev, type: value || undefined };
          return prev;
        },
        replace: false,
        resetScroll: false,
      });
    },
    [navigate],
  );
  const getDiscographyTypeLinkTarget = useCallback(
    (value: string) =>
      buildFilterLinkTarget({
        to: "/company/$id",
        params: { id },
        currentSearch: search,
        target: { key: "type", kind: "param", value },
      }),
    [id, search],
  );
  const getDiscographyFormatLinkTarget = useCallback(
    (value: string) =>
      buildFilterLinkTarget({
        to: "/company/$id",
        params: { id },
        currentSearch: search,
        target:
          value === "all"
            ? {
                config: COMPANY_DISCOGRAPHY_URL_CONFIG,
                key: "format",
                mode: "clear",
              }
            : {
                config: COMPANY_DISCOGRAPHY_URL_CONFIG,
                key: "format",
                mode: "toggle",
                value,
              },
      }),
    [id, search],
  );
  const sort = facetControls.sort as SortOption;
  const discographyQueryType = useDiscographyQueryType(search.type, serverDiscographyQueryType);
  const searchFilter = useTextInputFilter("q", defaultTo(search.q, ""), updateFilter);

  const t = useTranslations("label");
  const tc = useTranslations("globals.common");
  const locale = useLocale();

  useRecordView("company", id);

  const companyQuery = useQuery(getCompanyQueryOptions({ id, locale }));
  const computeCompanyBioResource = useCallback(
    () => orpc.music.computeCompanyBioResource.call({ companyId: id, locale }),
    [id, locale],
  );
  const { value: companyBio } = useDeferredResourceQuery({
    queryOptions: getCompanyBioResourceQueryOptions({
      companyId: id,
      locale,
    }),
    compute: computeCompanyBioResource,
  });

  const company = defaultTo(companyQuery.data, null);
  const isLoading = companyQuery.isLoading;

  if (isLoading) {
    return <Spinner variant="page" />;
  }

  if (companyQuery.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ErrorIcon className="text-6xl text-destructive mb-4" />
          <p className="text-xl font-medium">{t("errorLoading")}</p>
          <p className="text-muted-foreground mt-2">{companyQuery.error.message}</p>
          <Link to="/search" className="mt-4 inline-block text-primary hover:underline">
            {tc("backToSearch")}
          </Link>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <CompanyUnavailableIcon className="text-6xl text-muted-foreground mb-4" />
          <p className="text-xl font-medium">{t("notFound")}</p>
          <Link to="/search" className="mt-4 inline-block text-primary hover:underline">
            {tc("backToSearch")}
          </Link>
        </div>
      </div>
    );
  }

  const bio = defaultTo(companyBio, {
    profile: company.profile,
    profileHtml: company.profileHtml,
  });
  const availableTypeCounts = defaultTo(company.availableTypeCounts, discographyTypeCounts);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <article className="relative max-w-[1440px] mx-auto px-4 md:px-10 lg:px-20 pt-5 pb-8 md:pt-6">
        <CompanyHeader
          name={company.name}
          aliases={company.aliases}
          parentName={company.parentName}
          parentId={company.parentId}
          officialUrls={company.officialUrls}
          socialUrls={company.socialUrls}
          imageUrl={company.imageUrl}
          profile={bio.profile}
          profileHtml={bio.profileHtml}
          wikidataId={company.wikidataId}
          shortDescription={company.shortDescription}
          foundedYear={company.foundedYear}
          countryCodes={company.countryCodes}
          usageCategories={company.usageCategories}
          primaryUsageCategory={company.primaryUsageCategory}
          roleUsage={company.roleUsage}
          taxonomy={company.taxonomy}
          aside={
            <CompanyAbout
              companyId={id}
              companyName={company.name}
              officialUrls={company.officialUrls}
              socialUrls={company.socialUrls}
              otherUrls={company.otherUrls}
              sourceLinks={company.sourceLinks}
            />
          }
        />

        <CompanyDiscography
          companyId={id}
          companyName={company.name}
          discographyQueryType={discographyQueryType}
          discographyTypeCounts={availableTypeCounts}
          format={activeFormat}
          sort={sort}
          facetControls={facetControls}
          searchValue={searchFilter.value}
          onSearchChange={searchFilter.setValue}
          updateFilter={updateFilter}
          getTypeLinkTarget={getDiscographyTypeLinkTarget}
          getFormatLinkTarget={getDiscographyFormatLinkTarget}
        />
      </article>
    </div>
  );
}
